import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import { useBackupUserProfilesMutation, useMigrateUserProfilesMutation, useEnsureSchemaFieldsMutation } from '../../redux/adminApiSlice';

// Types for detailed report
interface DetailedReport {
  totals: {
    processed: number;
    modified: number;
    skipped: number;
    errors: number;
  };
  migrations: {
    profilePictureMigrated: number;
    altProfilePictureMigrated: number;
    profilePictureCleared: number;
    altProfilePictureCleared: number;
  };
  skippedDetails: {
    alreadyMigrated: number;
    noChangesNeeded: number;
  };
}

const ProfileMigration: React.FC = () => {
  // State for migration options
  const [batchSize, setBatchSize] = useState(50);
  const [dryRun, setDryRun] = useState(true);
  const [backupFilename, setBackupFilename] = useState('user_profiles_backup');
  
  // Status state
  const [progressStatus, setProgressStatus] = useState({ current: 0, total: 0 });
  const [status, setStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [detailedReport, setDetailedReport] = useState<DetailedReport | null>(null);

  // Mutations
  const [backupUserProfiles, { isLoading: isBackupLoading }] = useBackupUserProfilesMutation();
  const [migrateUserProfiles, { isLoading: isMigrateLoading }] = useMigrateUserProfilesMutation();
  const [ensureSchemaFields, { isLoading: isSchemaLoading }] = useEnsureSchemaFieldsMutation();

  const isLoading = isBackupLoading || isMigrateLoading || isSchemaLoading;

  const handleBackup = async () => {
    try {
      setStatus('running');
      setStatusMessage('Starting backup...');
      setDetailedReport(null);
      
      const response = await backupUserProfiles({ 
        filename: backupFilename 
      }).unwrap();
      
      setStatus('success');
      setStatusMessage(`Backup successful! ${response.message}`);
    } catch (error) {
      console.error('Backup failed:', error);
      setStatus('error');
      setStatusMessage(`Backup failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleMigrate = async () => {
    try {
      setStatus('running');
      setProgressStatus({ current: 0, total: 0 });
      setStatusMessage('Starting profile picture migration...');
      setDetailedReport(null);
      
      const response = await migrateUserProfiles({ 
        batchSize, 
        dryRun 
      }).unwrap();
      
      setProgressStatus(response.progress || { current: 0, total: 0 });
      setDetailedReport(response.detailedReport || null);
      setStatus('success');
      setStatusMessage(`Migration ${dryRun ? '(DRY RUN) ' : ''}completed! ${response.message}`);
    } catch (error) {
      console.error('Migration failed:', error);
      setStatus('error');
      setStatusMessage(`Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEnsureSchemaFields = async () => {
    try {
      setStatus('running');
      setStatusMessage('Syncing schema fields...');
      setDetailedReport(null);
      
      const response = await ensureSchemaFields().unwrap();
      
      setStatus('success');
      setStatusMessage(`Schema fields synced! ${response.message}`);
    } catch (error) {
      console.error('Schema sync failed:', error);
      setStatus('error');
      setStatusMessage(`Schema sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>User Profile Picture Migration</Typography>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>About This Migration</Typography>
          <Typography variant="body1" paragraph>
            This tool will migrate user profiles from the legacy profile picture structure to the new structure:
          </Typography>
          <Typography component="div" variant="body2" sx={{ mb: 2 }}>
            <ul>
              <li><strong>Legacy structure:</strong> stores <code>profilePicture</code> and <code>altProfilePicture</code> URLs</li>
              <li><strong>New structure:</strong> stores separate <code>facePicture</code> and <code>publicPicture</code> URLs</li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            The migration will:
          </Typography>
          <Typography component="div" variant="body2" sx={{ mb: 2 }}>
            <ul>
              <li>Copy <code>profilePicture</code> → <code>facePicture</code> (used for friends and when privacy allows)</li>
              <li>Copy <code>altProfilePicture</code> → <code>publicPicture</code> (used for non-friends based on privacy)</li>
              <li><strong>Important:</strong> If a user has no <code>altProfilePicture</code>, we will NOT set <code>publicPicture</code></li>
              <li>Set the original <code>profilePicture</code> field to an empty string</li>
              <li><strong>Completely remove</strong> the <code>altProfilePicture</code> field from user profiles (not just set to empty)</li>
            </ul>
          </Typography>
          <Typography variant="body1">
            This allows the application to determine which picture to show based on friendship status and privacy settings.
            The profile picture displayed in the app will be dynamically computed based on these new fields.
          </Typography>
        </CardContent>
      </Card>

      {/* Schema Sync Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>0. Ensure Schema Fields</Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          This step ensures all user documents in the database have the required fields for migration.
          Run this first to add the facePicture and publicPicture fields to the database schema.
        </Typography>
        
        <Button 
          variant="contained" 
          onClick={handleEnsureSchemaFields}
          disabled={isLoading}
          color="secondary"
          fullWidth
        >
          {isSchemaLoading ? <CircularProgress size={24} /> : 'Sync Schema Fields'}
        </Button>
      </Paper>

      <Grid container spacing={4}>
        {/* Backup Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>1. Backup User Profiles</Typography>
            <Typography variant="body2" paragraph color="text.secondary">
              Always create a backup before performing migration. This will export all user profiles to a JSON file.
            </Typography>
            
            <TextField
              fullWidth
              label="Backup Filename"
              value={backupFilename}
              onChange={(e) => setBackupFilename(e.target.value)}
              margin="normal"
              variant="outlined"
              disabled={isLoading}
              helperText="File will be saved with .json extension"
            />
            
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={handleBackup}
                disabled={isLoading || !backupFilename.trim()}
                color="primary"
                fullWidth
              >
                {isBackupLoading ? <CircularProgress size={24} /> : 'Backup User Profiles'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Migration Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>2. Migrate Profile Pictures</Typography>
            <Typography variant="body2" paragraph color="text.secondary">
              Run the migration to update user profile picture structure. We recommend running in dry-run mode first.
            </Typography>
            
            <TextField
              fullWidth
              label="Batch Size"
              type="number"
              value={batchSize}
              onChange={(e) => setBatchSize(Number(e.target.value))}
              margin="normal"
              variant="outlined"
              disabled={isLoading}
              helperText="Number of profiles to process in each batch"
              InputProps={{ inputProps: { min: 10, max: 500 } }}
            />
            
            <FormControlLabel
              control={
                <Switch 
                  checked={dryRun} 
                  onChange={(e) => setDryRun(e.target.checked)} 
                  disabled={isLoading}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Dry Run</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, shows what would change without making actual database updates
                  </Typography>
                </Box>
              }
              sx={{ mt: 1, display: 'flex', alignItems: 'flex-start' }}
            />
            
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                onClick={handleMigrate}
                disabled={isLoading}
                color={dryRun ? "info" : "warning"}
                fullWidth
              >
                {isMigrateLoading ? <CircularProgress size={24} /> : (
                  dryRun ? 'Run Migration (Dry Run)' : 'Run Migration (LIVE)'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Status Section */}
      {status !== 'idle' && (
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>Status</Typography>
          
          <Alert 
            severity={
              status === 'running' ? 'info' : 
              status === 'success' ? 'success' : 
              'error'
            }
            sx={{ mb: 2 }}
          >
            {statusMessage}
          </Alert>
          
          {status === 'running' && progressStatus.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Progress: {progressStatus.current} / {progressStatus.total} profiles processed
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(progressStatus.current / progressStatus.total) * 100} 
                  />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((progressStatus.current / progressStatus.total) * 100)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Detailed Report */}
          {status === 'success' && detailedReport && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>Detailed Migration Report</Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" color="primary" gutterBottom>Overall Statistics</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Total Profiles</TableCell>
                              <TableCell align="right">{detailedReport.totals.processed}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Modified</TableCell>
                              <TableCell align="right">{detailedReport.totals.modified}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Skipped</TableCell>
                              <TableCell align="right">{detailedReport.totals.skipped}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Errors</TableCell>
                              <TableCell align="right">{detailedReport.totals.errors}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" color="primary" gutterBottom>Field Migrations</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Profile Pictures Migrated</TableCell>
                              <TableCell align="right">{detailedReport.migrations.profilePictureMigrated}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Alt Profile Pictures Migrated</TableCell>
                              <TableCell align="right">{detailedReport.migrations.altProfilePictureMigrated}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Profile Pictures Cleared</TableCell>
                              <TableCell align="right">{detailedReport.migrations.profilePictureCleared}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Alt Profile Pictures Cleared</TableCell>
                              <TableCell align="right">{detailedReport.migrations.altProfilePictureCleared}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" color="primary" gutterBottom>Skipped Details</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Already Migrated</TableCell>
                              <TableCell align="right">{detailedReport.skippedDetails.alreadyMigrated}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>No Changes Needed</TableCell>
                              <TableCell align="right">{detailedReport.skippedDetails.noChangesNeeded}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ProfileMigration; 